import { Component, EventEmitter, Input, Output } from "@angular/core";
import { Fiat } from "@models";
import { TradableChartService } from "../../services/tradable-chart.service";
import { Tradable } from "@models/interfaces/tradable";
import { Index } from "@models/asset/index/index.model";
import { toCurrencyByFiatId } from "@helpers/tradable-convert";
import { Point } from "@models/point";
import { getPriceChange } from "@helpers/utils";
import { AccountService } from "../../../account/services/account.service";
import { ChartService } from "@main-services/chart/chart.service";
import { ITradableChartData } from "@modules/main/components/tradable-chart/config/tradable-chart-data.interface";
import { combineLatest, EMPTY, Observable, Subject } from "rxjs";
import { catchError, filter, map } from "rxjs/operators";
import { OhlcData } from "@models/charts/ohlc-data";
import { TranslationService } from "@core-services/translation.service";
import { LineChartConfig } from "@modules/shared/chart/models/chart-config.model";
import { Timespan } from "@helpers/const/timespan";

@Component({
	selector: "tradable-chart",
	templateUrl: "./tradable-chart.component.html",
	styleUrls: ["./tradable-chart.component.scss"],
	standalone: false
})
export class TradableChartComponent {
	public tradable: Tradable = null;
	public timespan: Timespan = Timespan.Day; // Year, week, month, day

	public lastTradingDayStart: string = "";
	public lastTradingDayEnd: string = "";

	public language$ = this.translationService.languageUpdate$();
	public data$: Subject<Point[]> = new Subject<Point[]>();
	public chartConfig$: Observable<LineChartConfig> = combineLatest([this.data$, this.language$]).pipe(
		map(([data, lang]) => ({
			tradable: this.tradable,
			data: data,
			language: lang,
			timespan: this.timespan,
			fiat: this.getFiat(),
			isSmall: true,
			lastTradingDayStart: this.lastTradingDayStart,
			lastTradingDayEnd: this.lastTradingDayEnd
		}))
	);

	@Input()
	public set data(data: ITradableChartData) {
		if (!data?.tradable || !data?.timespan) {
			return;
		}

		// If the tradable & the timespan hasn't changed we don't need to update the chart, but only the price
		if (data.tradable.getId() === this.tradable?.getId() && this.timespan === data.timespan) {
			this.tradable = data.tradable;
		} else {
			this.tradable = data.tradable;
			this.timespan = data.timespan;
			this.updateChartData();
		}
	}

	@Output() public onClick = new EventEmitter<Tradable>();

	public constructor(
		private accountService: AccountService,
		private tradableChartService: TradableChartService,
		private chartService: ChartService,
		private translationService: TranslationService
	) {}

	// Fetches new data + updates chart
	public updateChartData(): void {
		this.chartService
			.getOhlc(this.tradable, this.timespan)
			.pipe(
				filter((data: OhlcData) => !!data && !!data.candles),
				catchError(() => EMPTY)
			)
			.subscribe((data: OhlcData) => {
				const points = data.candles.map((c) => new Point(c.getTime().getUnix(), c.getClose()));

				this.lastTradingDayStart = data.lastTradingDayStart;
				this.lastTradingDayEnd = data.lastTradingDayEnd;

				this.data$.next(points);
			});
	}

	public isIndex(): boolean {
		return this.tradable instanceof Index;
	}

	public getChangeFiat(): number {
		return getPriceChange(this.tradable, this.timespan).changeAmount;
	}

	public getAbsoluteChange(): number {
		return toCurrencyByFiatId(Math.abs(this.getChangeFiat()), this.getFiat());
	}

	public getChangePercent(): number {
		return getPriceChange(this.tradable, this.timespan).changePercent;
	}

	public getAbsolutePercentChange(): number {
		return Math.abs(this.getChangePercent());
	}

	public getFiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public getFiatAmount(): number {
		if (!this.getFiat()) {
			return 0;
		}
		return this.isIndex() ? this.tradable.getPrice() : toCurrencyByFiatId(this.tradable.getPrice(), this.getFiat());
	}

	public onTradableClick(tradable: Tradable): void {
		this.onClick.emit(tradable);
	}
}
